import { FIRST_ACCOUNT_SETUP_STARTER_PROMPT } from "../../lib/firstAccountSetupStarterPrompt";

/**
 * Whether this host is a local QA harness.
 *
 * The `?qaPrompt=` seed exists so a browser test can land on `/new` with the
 * composer already filled. Gating it on the hostname keeps it from ever being a
 * way to prefill a real user's composer from a link.
 */
export function isLocalQaHost(hostname: string): boolean {
  return (
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname === "::1" ||
    hostname.endsWith(".localhost")
  );
}

/** The QA seed a local harness is asking for, if this host may honour one. */
export function resolveQaSeedPrompt(input: {
  readonly hostname: string;
  readonly search: string;
}): string | null {
  if (!isLocalQaHost(input.hostname)) return null;
  return new URLSearchParams(input.search).get("qaPrompt");
}

/**
 * The setup prompt to start the composer with, read once at mount.
 *
 * Two sources, and the query parameter wins: arriving from the setup flow is a
 * fresh instruction, while the stored copy is a leftover from a previous visit
 * that may already have been used.
 */
export function resolveInitialFirstAccountSetupPrompt(input: {
  readonly search: string;
  readonly storedPrompt: string | null;
}): string | null {
  if (new URLSearchParams(input.search).get("firstAccountSetupPrompt") === "1") {
    return FIRST_ACCOUNT_SETUP_STARTER_PROMPT;
  }
  return input.storedPrompt;
}

/**
 * What the first-account setup seed should do to the composer.
 *
 * `consumeStorage` and `clearSearchParam` are separate answers because the two
 * sources are consumed differently: the stored copy is deleted whenever it is
 * read at all, while the query parameter is stripped from the URL only when it
 * is the thing that asked — otherwise a reload of a plain `/new` would rewrite
 * history for no reason.
 */
export function resolveFirstAccountSetupSeed(input: {
  readonly search: string;
  readonly storedPrompt: string | null;
  readonly initialPrompt: string | null;
}): {
  readonly prompt: string | null;
  readonly clearSearchParam: boolean;
} {
  const requestedBySearch =
    new URLSearchParams(input.search).get("firstAccountSetupPrompt") === "1";
  const prompt =
    requestedBySearch || input.initialPrompt === FIRST_ACCOUNT_SETUP_STARTER_PROMPT
      ? FIRST_ACCOUNT_SETUP_STARTER_PROMPT
      : input.storedPrompt;
  return { prompt, clearSearchParam: requestedBySearch };
}
